/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useCallback, useEffect, useState} from 'react';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';

import {images} from '../../../../constants/image';
import {
  Modal,
  Select,
  Button,
  Input,
  DateTimePicker,
  RadioGroups,
} from '../../../../components';

// style
import {
  Typography,
  StyledIconClose,
  StyledModalTitle,
  StyledModalActions,
  Divider,
} from './ModalFilter.style';
import {
  DISTRIBUTION_PLATFORMS_CONSTANT,
  DISTRIBUTION_PLATFORMS_OPTIONS,
  ISLANDER_MEMBERS_SELECTION_TYPE_CONSTANT,
  ISLANDER_MEMBERS_SELECTION_TYPE_OPTIONS,
} from '../../DistributionLogs';
import {getListTicketAvailable} from '../../../../utils/targeted-distributions/TargetedDistributionsUtils';
import {SelectOptionsProps} from '../../../../types/typeTargetedDistributions';
import SingleSelectAutocomplete from '../../../../components/Selects/SingleSelect/SingleSelect';
import {every, isEmpty, omit} from 'lodash';
import {formatDateTime} from '../../../../utils';

interface ModalFilterProps {
  visible: boolean;
  onClose?: () => void;
  handleFilter?: () => void;
  handleClean?: () => void;
  defaultValueFilter?: any;
  valueFilter?: any;
  setValueFilter?: (e: any) => void;
  valueFilterLabels: any;
  setValueFilterLabels: (e: any) => void;
}

interface DateTimeRange {
  startDate?: Date | null;
  endDate?: Date | null;
}

function ModalFilter({
  visible,
  onClose = () => {},
  handleFilter = () => {},
  handleClean = () => {},
  defaultValueFilter,
  valueFilter,
  setValueFilter = (e: any) => {},
  setValueFilterLabels = (e: any) => {},
}: ModalFilterProps) {
  const formatTime = 'DD/MM/YYYY HH:mm';
  const DEFAULT_VALUE_RANGE_PICKER = {
    startDate: null,
    endDate: null,
  };

  const [vouchersList, setvouchersList] = useState<any[]>([]);
  const [selectedVoucher, setSelectedVoucher] = useState<any | undefined>(
    undefined
  );
  const [valueRangePicker, setValueRangePicker] = useState<DateTimeRange>(
    DEFAULT_VALUE_RANGE_PICKER
  );

  const handleChangeValueRangePicker = (startDate, endDate) => {
    const localStartDate = new Date(startDate);
    let localEndDate = endDate && new Date(endDate);
    localEndDate =
      localEndDate && startDate > localEndDate ? null : localEndDate;
    if (localStartDate) {
      if (!localEndDate) {
        localEndDate = new Date(localStartDate);
        localEndDate.setHours(23);
        localEndDate.setMinutes(59);
        localEndDate.setSeconds(59);
        localEndDate.setMilliseconds(999);
      }
    } else {
      localEndDate = null;
    }
    const data = {
      startDate: localStartDate,
      endDate: localEndDate,
    };
    setValueRangePicker(data);
    return data;
  };

  useEffect(() => {
    if (valueFilter.createdDateTime_gte || valueFilter.createdDateTime_lte) {
      handleChangeValueRangePicker(
        valueFilter.createdDateTime_gte,
        valueFilter.createdDateTime_lte
      );
    } else {
      setValueRangePicker(DEFAULT_VALUE_RANGE_PICKER);
    }
  }, [valueFilter]);

  useEffect(() => {
    if (!isEmpty(vouchersList)) {
      if (valueFilter?.targetedTicketId_eq) {
        const foundSelectedVoucher = vouchersList.find(
          voucher => voucher.id === valueFilter?.targetedTicketId_eq
        );
        if (foundSelectedVoucher) {
          setSelectedVoucher(foundSelectedVoucher);
          setValueFilterLabels({
            targetedTicketId_eq: foundSelectedVoucher?.label ?? '',
          });
        }
      } else if (selectedVoucher) {
        setSelectedVoucher(undefined);
      }
    }
  }, [valueFilter, vouchersList]);

  const onChange = (event: any) => {
    const {name, value} = event.target;
    const newValueFilter: any = {
      [name]: value,
    };
    if (name === 'distributionplatform_eq') {
      if (value === DISTRIBUTION_PLATFORMS_CONSTANT.SFMC) {
        if (
          valueFilter.targetedMembershipType_eq ===
          ISLANDER_MEMBERS_SELECTION_TYPE_CONSTANT.MULTIPLE_SPECIFIC_MEMBERS
        ) {
          newValueFilter.targetedMembershipType_eq = '';
        }
        newValueFilter.sFMCDefinitionInstanceID_eq =
          defaultValueFilter.sFMCDefinitionInstanceID_eq;
        newValueFilter.sFMCJourneyID_eq = defaultValueFilter.sFMCJourneyID_eq;
        newValueFilter.sFMCJourneyActivityID_eq =
          defaultValueFilter.sFMCJourneyActivityID_eq;
      } else {
        newValueFilter.sFMCDefinitionInstanceID_eq = '';
        newValueFilter.sFMCJourneyID_eq = '';
        newValueFilter.sFMCJourneyActivityID_eq = '';
      }
    }
    if (name === 'targetedMembershipType_eq') {
      if (
        value ===
        ISLANDER_MEMBERS_SELECTION_TYPE_CONSTANT.MULTIPLE_SPECIFIC_MEMBERS
      ) {
        newValueFilter.targetedMembershipId_eq = '';
      } else {
        newValueFilter.targetedMembershipId_eq =
          defaultValueFilter.targetedMembershipId_eq;
      }
    }
    setValueFilter((prevState: any) => {
      return {
        ...prevState,
        ...newValueFilter,
      };
    });
  };

  const getVouchers = async () => {
    const ticketRes = await getListTicketAvailable();
    if (ticketRes?.data && Array.isArray(ticketRes.data.items)) {
      const listOptions: SelectOptionsProps[] = ticketRes?.data?.items.map(
        t => {
          return {
            label: `[${t.numberId}] ${t.title} | ${t.excerpt}`,
            value: t.numberId,
            id: t.id,
            targetedDistribution: t.targetedDistribution,
          };
        }
      );
      setvouchersList(listOptions.sort((a, b) => a.value - b.value));
    }
  };

  const handleCleanModalForm = () => {
    setSelectedVoucher(undefined);
    setValueRangePicker(DEFAULT_VALUE_RANGE_PICKER);
    handleClean();
  };

  const shouldDisableApplyFilterButton = () => {
    const isTargetedMembershipTypeInvalid =
      valueFilter.targetedMembershipType_eq ===
        ISLANDER_MEMBERS_SELECTION_TYPE_CONSTANT.SINGLE_SPECIFIC_MEMBER &&
      !valueFilter.targetedMembershipId_eq;
    const isOtherFieldsInvalid = every(
      omit(valueFilter, ['distributionplatform_eq']),
      valueFilterItem => isEmpty(valueFilterItem)
    );
    return isTargetedMembershipTypeInvalid || isOtherFieldsInvalid;
  };

  const getTargetedMembershipTypeOptions = useCallback(
    () =>
      ISLANDER_MEMBERS_SELECTION_TYPE_OPTIONS.filter(
        option =>
          valueFilter.distributionplatform_eq ===
            DISTRIBUTION_PLATFORMS_CONSTANT.ADMIN_PORTAL ||
          option.value !==
            ISLANDER_MEMBERS_SELECTION_TYPE_CONSTANT.MULTIPLE_SPECIFIC_MEMBERS
      ),
    [valueFilter]
  );

  // useEffect(() => {
  //   const updatedValueFilter: any = {};
  //   if (valueRangePicker.startDate) {
  //     updatedValueFilter.createdDateTime_gte = formatDateTime(
  //       valueRangePicker.startDate,
  //       formatTime
  //     );
  //   }
  //   if (valueRangePicker.endDate) {
  //     updatedValueFilter.createdDateTime_lte = formatDateTime(
  //       valueRangePicker.endDate,
  //       formatTime
  //     );
  //   }
  //   if (!isEmpty(updatedValueFilter)) {
  //     setValueFilter(prevState => ({
  //       ...prevState,
  //       ...updatedValueFilter,
  //     }));
  //   }
  // }, [valueRangePicker]);

  useEffect(() => {
    if (visible) {
      getVouchers();
    }
  }, [visible]);

  return (
    <Modal visible={visible} onClose={onClose} maxWidth="sm">
      <>
        <StyledModalTitle>Distribution logs – Filter by</StyledModalTitle>
        <StyledIconClose onClick={onClose}>
          <img src={images.icon_close} />
        </StyledIconClose>
        <Divider variant="middle" />
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <Typography variant="subtitle2" mb={2}>
              Distribution platform
            </Typography>
            <RadioGroups
              values={valueFilter.distributionplatform_eq}
              options={DISTRIBUTION_PLATFORMS_OPTIONS}
              onChange={(e, value) =>
                onChange({
                  target: {
                    name: 'distributionplatform_eq',
                    value,
                  },
                })
              }
            />
          </FormControl>
          <FormControl fullWidth margin="dense">
            <Typography variant="subtitle2" mb={2}>
              Admin email executed this distribution
            </Typography>
            <Input
              type="text"
              name="email_eq"
              placeholder="Enter admin email"
              size="small"
              variant="outlined"
              value={valueFilter.email_eq}
              onChange={onChange}
            />
          </FormControl>
          {valueFilter.distributionplatform_eq ===
            DISTRIBUTION_PLATFORMS_CONSTANT.SFMC && (
            <>
              <FormControl fullWidth margin="dense">
                <Typography variant="subtitle2" mb={2}>
                  SFMC Definition Instance ID
                </Typography>
                <Input
                  type="text"
                  name="sFMCDefinitionInstanceID_eq"
                  placeholder="Enter SFMC Definition Instance ID"
                  size="small"
                  variant="outlined"
                  value={valueFilter.sFMCDefinitionInstanceID_eq}
                  onChange={onChange}
                />
              </FormControl>
              <FormControl fullWidth margin="dense">
                <Typography variant="subtitle2" mb={2}>
                  SFMC Journey ID
                </Typography>
                <Input
                  type="text"
                  name="sFMCJourneyID_eq"
                  placeholder="Enter Enter SFMC Journey ID"
                  size="small"
                  variant="outlined"
                  value={valueFilter.sFMCJourneyID_eq}
                  onChange={onChange}
                />
              </FormControl>
              <FormControl fullWidth margin="dense">
                <Typography variant="subtitle2" mb={2}>
                  SFMC Journey Activity ID
                </Typography>
                <Input
                  type="text"
                  name="sFMCJourneyActivityID_eq"
                  placeholder="Enter Enter SFMC Journey Activity ID"
                  size="small"
                  variant="outlined"
                  value={valueFilter.sFMCJourneyActivityID_eq}
                  onChange={onChange}
                />
              </FormControl>
            </>
          )}
          <FormControl fullWidth margin="dense" sx={{mb: 5.5}}>
            <Typography variant="subtitle2" mb={2}>
              Distribution start time
            </Typography>
            <DateTimePicker
              isEmptyValue={valueRangePicker.startDate === null}
              value={valueRangePicker.startDate}
              onChange={e => {
                const {startDate, endDate} = handleChangeValueRangePicker(
                  e,
                  valueRangePicker.endDate
                );
                const updatedValueFilter: any = {};
                if (startDate) {
                  updatedValueFilter.createdDateTime_gte = formatDateTime(
                    startDate,
                    formatTime
                  );
                }
                if (endDate) {
                  updatedValueFilter.createdDateTime_lte = formatDateTime(
                    endDate,
                    formatTime
                  );
                }
                if (!isEmpty(updatedValueFilter)) {
                  setValueFilter(prevState => ({
                    ...prevState,
                    ...updatedValueFilter,
                  }));
                }
              }}
            />
          </FormControl>
          <FormControl fullWidth margin="dense" sx={{mb: 5.5}}>
            <Typography variant="subtitle2" mb={2}>
              Distribution end time
            </Typography>
            <DateTimePicker
              disabled={valueRangePicker.startDate === null}
              isEmptyValue={valueRangePicker.endDate === null}
              value={valueRangePicker.endDate}
              onChange={e => {
                setValueFilter(prevState => ({
                  ...prevState,
                  createdDateTime_lte: formatDateTime(e, formatTime),
                }));
                setValueRangePicker({
                  ...valueRangePicker,
                  endDate: e,
                });
              }}
            />
          </FormControl>
          <FormControl fullWidth margin="dense" sx={{mb: 5.5}}>
            <Typography variant="subtitle2" mb={2}>
              Voucher distributed
            </Typography>
            <SingleSelectAutocomplete
              name="targetedTicketId_eq"
              placeholder="Select voucher"
              options={vouchersList}
              value={selectedVoucher}
              onChange={(e, newValue) => {
                setSelectedVoucher(newValue);
                setValueFilter((prevState: any) => ({
                  ...prevState,
                  targetedTicketId_eq: newValue?.id ?? '',
                }));
                setValueFilterLabels((prevState: any) => ({
                  ...prevState,
                  targetedTicketId_eq: newValue?.label ?? '',
                }));
              }}
            />
          </FormControl>
          <FormControl fullWidth margin="dense" sx={{mb: 5.5}}>
            <Typography variant="subtitle2" mb={2}>
              Islander member(s) selection type
            </Typography>
            <Select
              labelId="targetedMembershipType_eq"
              displayEmpty
              name="targetedMembershipType_eq"
              variant="outlined"
              onChangeSelect={onChange}
              value={valueFilter.targetedMembershipType_eq}
              placeholder="Select type"
              size="small"
              selectOptions={getTargetedMembershipTypeOptions()}
            />
          </FormControl>
          {valueFilter.targetedMembershipType_eq ===
            ISLANDER_MEMBERS_SELECTION_TYPE_CONSTANT.SINGLE_SPECIFIC_MEMBER && (
            <FormControl fullWidth margin="dense">
              <Typography variant="subtitle2" mb={2}>
                Membership ID
              </Typography>
              <Input
                type="text"
                name="targetedMembershipId_eq"
                placeholder="Enter Membership ID"
                size="small"
                variant="outlined"
                value={valueFilter.targetedMembershipId_eq}
                onChange={onChange}
              />
            </FormControl>
          )}
        </DialogContent>
        <Divider variant="middle" />
        <StyledModalActions>
          <div className="btn-action-1">
            <Button variant="text" onClick={handleCleanModalForm}>
              Clear
            </Button>
          </div>
          <div className="btn-action-2">
            <Button
              onClick={handleFilter}
              disabled={shouldDisableApplyFilterButton()}
            >
              Apply Filter
            </Button>
          </div>
        </StyledModalActions>
      </>
    </Modal>
  );
}
export default ModalFilter;
